import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Tooltip, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import DataKitApi from "../../../../api/DataKitApi";
import {
  DataKitDto,
  DataKitItemDto,
  DataKitItemModifierGroupDto,
  DataKitItemPriceDto,
  ItemMenuDto,
  MenuSummaryDto,
} from "../../../../domain/DataKitDto";
import "./DataKitOverrideTables.scss";

interface LocationLike {
  internalId?: number;
  name?: string;
  city?: string;
  state?: string;
}

/**
 * Human label for a location price a given menu will not be using.
 *
 * The customer-facing wording deliberately never says wins, resolves or override: a location price
 * simply covers every menu that has no price of its own. The chip states the exception; the full
 * sentence lives on hover.
 */
function exceptionCopy(menuNames: string[]): string {
  if (menuNames.length === 1) return `every menu except ${menuNames[0]}`;
  const last = menuNames[menuNames.length - 1];
  return `every menu except ${menuNames.slice(0, -1).join(", ")} and ${last}`;
}

/**
 * One menu's own price across every size, as the customer reads it: "$25 / $46". A size the menu
 * does not price itself shows the kit price, because that is what the menu will actually print --
 * a blank cell inherits, so quoting only the overridden sizes would misstate the menu.
 */
function menuPriceLabel(menuId: string, prices: DataKitItemPriceDto[]): string {
  return prices
    .map((p) => {
      const own = (p.menuOverrides || []).find((o) => o.menuId === menuId);
      return `${(own && own.price) || p.price || ""}`.trim();
    })
    .filter(Boolean)
    .join(" / ");
}

/**
 * The hover sentence behind the chip. Names the menus that price themselves, quotes what they
 * charge, and says plainly what the location price still covers.
 */
function exceptionSentence(priced: { name: string; label: string }[], locationName: string): string {
  const withPrice = priced.map((m) => (m.label ? `${m.name} (${m.label})` : m.name));
  if (withPrice.length === 1) {
    return `${withPrice[0]} has its own price. Every other ${locationName} menu shows this price.`;
  }
  const last = withPrice[withPrice.length - 1];
  const list = `${withPrice.slice(0, -1).join(", ")} and ${last}`;
  return `${list} have their own prices. Every other ${locationName} menu shows this price.`;
}

interface Props {
  kit: DataKitDto;
  item: DataKitItemDto;
  prices: DataKitItemPriceDto[];
  /** The drawer's modifier groups - their priced options get a column in the location table. */
  modifierGroups: DataKitItemModifierGroupDto[];
  locations: LocationLike[];
  /** The menus (designs) using this library - the menu picker's "other menus". */
  menus: MenuSummaryDto[];
  canWrite: boolean;
  /** Called after a price write lands, so the drawer can take the server's item back. */
  onItemChanged: (item: DataKitItemDto) => void;
  onLocationOverridesChanged: (next: DataKitItemPriceDto[]) => void;
  onModifierGroupsChanged: (next: DataKitItemModifierGroupDto[]) => void;
}

/** One priced modifier option - a column in the location price table (Oct 12). */
interface ModifierColumn {
  groupIndex: number;
  index: number;
  name: string;
  libraryPrice: string;
}

/**
 * The two override tables under the item's own prices: one row per location, one row per menu, one
 * cell per size. A blank cell inherits, which is why the kit price sits in the column header as a
 * grey reference rather than being pre-filled into the input.
 */
export function DataKitOverrideTables({
  kit,
  item,
  prices,
  modifierGroups,
  locations,
  menus,
  canWrite,
  onItemChanged,
  onLocationOverridesChanged,
  onModifierGroupsChanged,
}: Props) {
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [draftId, setDraftId] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  // The menus showing this item - listed first in the menu picker (Oct 12: menus are designs).
  const [itemMenus, setItemMenus] = useState<ItemMenuDto[]>([]);

  useEffect(() => {
    if (!item.id) return;
    let cancelled = false;
    DataKitApi.listItemMenus(item.id)
      .then((res) => {
        if (!cancelled) setItemMenus(res.data || []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [item.id]);

  const locationNameById = useMemo(() => {
    const map = new Map<string, string>();
    locations.forEach((l) => {
      if (l.internalId != null) {
        const parts = [l.city, l.state].filter(Boolean);
        map.set(String(l.internalId), l.name || parts.join(", ") || `Location ${l.internalId}`);
      }
    });
    return map;
  }, [locations]);

  /**
   * A menu's location, named the way this account's own locations are (Oct 13, QA: menus showed no
   * location) - shared's label is only the fallback. See menuLocation.ts.
   */
  const menuLocationName = useCallback(
    (m: { locationId?: number | null; locationName?: string | null }) =>
      (m.locationId != null ? locationNameById.get(String(m.locationId)) : undefined) || m.locationName || null,
    [locationNameById]
  );

  /** A menu is a design: its name, and the location it belongs to. */
  const menuById = useMemo(() => {
    const map = new Map<string, { name: string; locationName?: string | null }>();
    menus.forEach((m) => map.set(m.designId, { name: m.name, locationName: menuLocationName(m) }));
    itemMenus.forEach((m) => map.set(m.designId, { name: m.name, locationName: menuLocationName(m) }));
    return map;
  }, [menus, itemMenus, menuLocationName]);

  const menuNameById = useMemo(
    () => new Map(Array.from(menuById.entries()).map(([id, m]) => [id, m.name])),
    [menuById]
  );

  /**
   * Modifier options with a price - each gets a column, so an admin can set a location's modifier
   * prices during setup, before any design exists (Oct 12). An option priced only at some location
   * still counts, or clearing its library price would hide the location prices it has.
   */
  const modifierColumns = useMemo(() => {
    const columns: ModifierColumn[] = [];
    modifierGroups.forEach((group, groupIndex) =>
      (group.modifiers || []).forEach((m, index) => {
        if (!(m.name || "").trim()) return;
        const priced =
          (m.price || "").trim() !== "" || (m.overrides || []).some((o) => (o.price || "").trim() !== "");
        if (priced) columns.push({ groupIndex, index, name: m.name.trim(), libraryPrice: (m.price || "").trim() });
      })
    );
    return columns;
  }, [modifierGroups]);

  /** Locations that already carry a price of their own - on a size, or on a modifier option. */
  const overriddenUnitIds = useMemo(() => {
    const ids = new Set<string>();
    prices.forEach((p) => (p.overrides || []).forEach((o) => ids.add(o.unitId)));
    modifierGroups.forEach((g) =>
      (g.modifiers || []).forEach((m) => (m.overrides || []).forEach((o) => ids.add(o.unitId)))
    );
    return Array.from(ids).sort((a, b) =>
      (locationNameById.get(a) || a).localeCompare(locationNameById.get(b) || b)
    );
  }, [prices, modifierGroups, locationNameById]);

  /** Menus that already carry a price on at least one size. */
  const overriddenMenuIds = useMemo(() => {
    const ids = new Set<string>();
    prices.forEach((p) => (p.menuOverrides || []).forEach((o) => ids.add(o.menuId)));
    return Array.from(ids).sort((a, b) =>
      (menuNameById.get(a) || a).localeCompare(menuNameById.get(b) || b)
    );
  }, [prices, menuNameById]);

  /** Menus this item prices itself — the exception a location row has to declare. */
  const pricedMenus = useMemo(
    () =>
      overriddenMenuIds.map((id) => ({
        name: menuNameById.get(id) || "a menu",
        label: menuPriceLabel(id, prices),
      })),
    [overriddenMenuIds, menuNameById, prices]
  );

  const pricedMenuNames = useMemo(() => pricedMenus.map((m) => m.name), [pricedMenus]);

  // The kit's own assigned-location count, not the account's location list: a location the kit does
  // not serve is not inheriting anything from it.
  const inheritingCount = Math.max(0, kit.locationCount - overriddenUnitIds.length);

  // By position, not size: two unlabelled prices share the size "", and typing in one used to fill
  // both (Oct 15, QA).
  const setLocationCell = (unitId: string, index: number, value: string) => {
    onLocationOverridesChanged(
      prices.map((p, i) => {
        if (i !== index) return p;
        const existing = (p.overrides || []).find((o) => o.unitId === unitId);
        if (value.trim() === "") {
          return { ...p, overrides: (p.overrides || []).filter((o) => o.unitId !== unitId) };
        }
        if (existing) {
          return {
            ...p,
            overrides: (p.overrides || []).map((o) =>
              o.unitId === unitId ? { ...o, price: value } : o
            ),
          };
        }
        return {
          ...p,
          overrides: [...(p.overrides || []), { unitType: "LOCATION", unitId, price: value }],
        };
      })
    );
  };

  const setModifierCell = (unitId: string, column: ModifierColumn, value: string) => {
    onModifierGroupsChanged(
      modifierGroups.map((group, gi) =>
        gi !== column.groupIndex
          ? group
          : {
              ...group,
              modifiers: group.modifiers.map((m, mi) => {
                if (mi !== column.index) return m;
                const rest = (m.overrides || []).filter((o) => o.unitId !== unitId);
                return { ...m, overrides: value.trim() === "" ? rest : [...rest, { unitId, price: value }] };
              }),
            }
      )
    );
  };

  /** Removing a location row removes its item and modifier prices together. */
  const removeLocationRow = (unitId: string) => {
    onLocationOverridesChanged(
      prices.map((p) => ({
        ...p,
        overrides: (p.overrides || []).filter((o) => o.unitId !== unitId),
      }))
    );
    onModifierGroupsChanged(
      modifierGroups.map((group) => ({
        ...group,
        modifiers: group.modifiers.map((m) =>
          (m.overrides || []).some((o) => o.unitId === unitId)
            ? { ...m, overrides: (m.overrides || []).filter((o) => o.unitId !== unitId) }
            : m
        ),
      }))
    );
  };

  const commitMenuCell = (menuId: string, price: DataKitItemPriceDto, value: string) => {
    if (!item.id) return;
    setSaving(true);
    DataKitApi.setMenuPrice(item.id, { menuId, size: price.size, priceId: price.id, price: value.trim() || null })
      .then((res) => onItemChanged(res.data))
      .catch(() => message.error("Could not save that menu price"))
      .finally(() => setSaving(false));
  };

  const addLocationRow = () => {
    if (!draftId) return;
    // Seeded blank, not pre-filled with the kit price: a blank cell means "inherit", so pre-filling
    // would silently pin this location to today's kit price.
    onLocationOverridesChanged(
      prices.map((p, i) =>
        i === 0
          ? {
              ...p,
              overrides: [
                ...(p.overrides || []),
                { unitType: "LOCATION", unitId: draftId, price: "" },
              ],
            }
          : p
      )
    );
    setAddLocationOpen(false);
    setDraftId(undefined);
  };

  const addMenuRow = () => {
    if (!draftId || !item.id || prices.length === 0) return;
    setSaving(true);
    DataKitApi.setMenuPrice(item.id, {
      menuId: draftId,
      size: prices[0].size,
      priceId: prices[0].id,
      price: prices[0].price,
    })
      .then((res) => {
        onItemChanged(res.data);
        setAddMenuOpen(false);
        setDraftId(undefined);
      })
      .catch(() => message.error("Could not add that menu price"))
      .finally(() => setSaving(false));
  };

  const sizeLabel = (p: DataKitItemPriceDto) => p.size?.trim() || "Price";

  /** The item's own menus first, then the library's other menus - "Design · Location" rows. */
  const menuPickerOptions = useMemo(() => {
    const label = (name: string, locationName?: string | null) =>
      locationName ? `${name} · ${locationName}` : name;
    const onItem = itemMenus
      .filter((m) => !overriddenMenuIds.includes(m.designId))
      .map((m) => ({ label: label(m.name, menuLocationName(m)), value: m.designId }));
    const onItemIds = new Set(itemMenus.map((m) => m.designId));
    const others = menus
      .filter((m) => !onItemIds.has(m.designId) && !overriddenMenuIds.includes(m.designId))
      .map((m) => ({ label: label(m.name, menuLocationName(m)), value: m.designId }));
    return [
      ...(onItem.length ? [{ label: "Menus this item is on", options: onItem }] : []),
      ...(others.length ? [{ label: "Other menus using this library", options: others }] : []),
    ];
  }, [itemMenus, menus, overriddenMenuIds, menuLocationName]);

  return (
    <>
      <section className="data-kit-override-panel">
        <div className="override-panel-head">
          <span className="override-panel-title">Location Price Overrides</span>
          <span className="override-panel-hint">
            Per size. {inheritingCount} location{inheritingCount === 1 ? "" : "s"} inherit the library
            price.
          </span>
        </div>

        {overriddenUnitIds.length > 0 && (
          <div className="override-table">
            <div className="override-row override-head">
              <span className="override-cell-label">Location</span>
              {prices.map((p, i) => (
                <span className="override-cell" key={i}>
                  {sizeLabel(p)} <em>library {p.price || "--"}</em>
                </span>
              ))}
              {modifierColumns.map((c) => (
                <span className="override-cell override-cell-modifier" key={`m:${c.groupIndex}:${c.index}`}>
                  {c.name} <em>library {c.libraryPrice || "--"}</em>
                </span>
              ))}
              <span className="override-cell-action" />
            </div>

            {overriddenUnitIds.map((unitId) => (
              <div className="override-row" key={unitId}>
                <span className="override-cell-label">
                  {locationNameById.get(unitId) || `Location ${unitId}`}
                  {pricedMenus.length > 0 && (
                    <Tooltip
                      title={exceptionSentence(
                        pricedMenus,
                        locationNameById.get(unitId) || "location"
                      )}
                    >
                      <span className="override-exception-chip">
                        {exceptionCopy(pricedMenuNames)}
                      </span>
                    </Tooltip>
                  )}
                </span>
                {prices.map((p, i) => {
                  const cell = (p.overrides || []).find((o) => o.unitId === unitId);
                  return (
                    <span className="override-cell" key={i}>
                      <Input
                        size="small"
                        value={cell?.price || ""}
                        placeholder={p.price || "--"}
                        disabled={!canWrite}
                        onChange={(e) => setLocationCell(unitId, i, e.target.value)}
                      />
                    </span>
                  );
                })}
                {modifierColumns.map((c) => {
                  const option = modifierGroups[c.groupIndex]?.modifiers[c.index];
                  const cell = (option?.overrides || []).find((o) => o.unitId === unitId);
                  return (
                    <span className="override-cell override-cell-modifier" key={`m:${c.groupIndex}:${c.index}`}>
                      <Input
                        size="small"
                        value={cell?.price || ""}
                        placeholder={c.libraryPrice || "--"}
                        disabled={!canWrite}
                        aria-label={`${c.name} price at ${locationNameById.get(unitId) || "this location"}`}
                        onChange={(e) => setModifierCell(unitId, c, e.target.value)}
                      />
                    </span>
                  );
                })}
                <span className="override-cell-action">
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => removeLocationRow(unitId)}
                      aria-label="Remove this location's prices"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {canWrite && (
          <Button
            className="override-add-btn secondary"
            onClick={() => {
              setDraftId(undefined);
              setAddLocationOpen(true);
            }}
          >
            + Location override
          </Button>
        )}
      </section>

      <section className="data-kit-override-panel">
        <div className="override-panel-head">
          <span className="override-panel-title">Menu Price Overrides</span>
          <span className="override-panel-hint">
            Per size. Each menu is a design, and its price shows on that design only.
          </span>
        </div>

        {overriddenMenuIds.length > 0 && (
          <div className="override-table">
            <div className="override-row override-head">
              <span className="override-cell-label">Menu</span>
              {prices.map((p, i) => (
                <span className="override-cell" key={i}>
                  {sizeLabel(p)} <em>library {p.price || "--"}</em>
                </span>
              ))}
              <span className="override-cell-action" />
            </div>

            {overriddenMenuIds.map((menuId) => (
              <div className="override-row" key={menuId}>
                <span className="override-cell-label">
                  {menuNameById.get(menuId) || "Menu"}
                  {menuById.get(menuId)?.locationName && (
                    <span className="override-cell-sub">{menuById.get(menuId)?.locationName}</span>
                  )}
                </span>
                {prices.map((p, i) => {
                  const cell = (p.menuOverrides || []).find((o) => o.menuId === menuId);
                  return (
                    <span className="override-cell" key={i}>
                      <Input
                        size="small"
                        defaultValue={cell?.price || ""}
                        placeholder={p.price || "--"}
                        disabled={!canWrite || saving}
                        onBlur={(e) => commitMenuCell(menuId, p, e.target.value)}
                        onPressEnter={(e) =>
                          commitMenuCell(menuId, p, (e.target as HTMLInputElement).value)
                        }
                      />
                    </span>
                  );
                })}
                <span className="override-cell-action">
                  {canWrite && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => prices.forEach((p) => commitMenuCell(menuId, p, ""))}
                      aria-label="Remove this menu's prices"
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {canWrite && (
          <Button
            className="override-add-btn secondary"
            onClick={() => {
              setDraftId(undefined);
              setAddMenuOpen(true);
            }}
          >
            + Menu override
          </Button>
        )}
      </section>

      <Modal
        open={addLocationOpen}
        width={520}
        className={"clean one"}
        onCancel={() => setAddLocationOpen(false)}
      >
        <header>
          <h2>Add a location price</h2>
        </header>
        <p className="override-modal-hint">
          Leave a size blank and that size keeps the library price at this location.
        </p>
        <Select
          className="override-modal-select"
          size="large"
          showSearch
          placeholder="Choose a location"
          value={draftId}
          onChange={setDraftId}
          optionFilterProp="label"
          options={locations
            .filter((l) => l.internalId != null && !overriddenUnitIds.includes(String(l.internalId)))
            .map((l) => ({
              label: l.name || [l.city, l.state].filter(Boolean).join(", "),
              value: String(l.internalId),
            }))}
        />

        <footer>
          <Button size="large" onClick={() => setAddLocationOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            disabled={!draftId}
            onClick={addLocationRow}
          >
            Add
          </Button>
        </footer>
      </Modal>

      <Modal
        open={addMenuOpen}
        width={520}
        className={"clean one"}
        onCancel={() => setAddMenuOpen(false)}
      >
        <header>
          <h2>Add a menu price</h2>
        </header>
        <p className="override-modal-hint">
          Starts from the library price so you can adjust it. Each menu is a design; this price shows
          on that design only.
        </p>
        <Select
          className="override-modal-select"
          size="large"
          showSearch
          placeholder="Choose a menu"
          value={draftId}
          onChange={setDraftId}
          optionFilterProp="label"
          options={menuPickerOptions}
        />

        <footer>
          <Button size="large" onClick={() => setAddMenuOpen(false)}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            loading={saving}
            disabled={!draftId}
            onClick={addMenuRow}
          >
            Add
          </Button>
        </footer>
      </Modal>
    </>
  );
}
